<template>
  <div
    v-on-clickaway="hideDropdown"
    :class="classes"
  >
    <!-- FIELD -->
    <div
      class="datepicker__field-wrapper"
      @click="toggleDropdownVisibility"
    >
      <label v-if="label" class="datepicker__label">
        {{ label }}
        <span v-if="required" class="datepicker__label-required">*</span>
      </label>

      <div class="datepicker__field">
        <div class="datepicker__text">
          <span class="datepicker__date">{{ formatDate(date) }}</span>
          <span class="datepicker__time">{{ time }}</span>
        </div>
        <ChevronIcon class="datepicker__arrow" />
      </div>
    </div>
    <!-- FIELD -->

    <!-- DROPDOWN -->
    <transition name="calendar-transition">
      <div
        v-if="isDropdownVisible"
        class="datepicker__dropdown"
      >
        <!-- CALENDAR -->
        <transition :name="dropdownInsideTransitionName" mode="out-in">
          <div
            v-if="!isPickingTime"
            key="date-picker"
            class="datepicker__dropdown-date"
          >
            <datepicker
              :value="date"
              v-bind="datepickerOptions"
              @input="onDateChange"
            />
            <div class="datepicker__dropdown-button-container">
              <BaseButton
                class="datepicker__dropdown-button"
                type="secondary"
                @click="onPickTime"
              >
                {{ buttonsText.pickTime }}
              </BaseButton>
            </div>
          </div>
          <!-- CALENDAR -->

          <!-- TIME -->
          <div
            v-else
            key="time-picker"
            class="datepicker__dropdown-time"
          >
            <div class="datepicker__dropdown-time-title">{{ formatDate(date) }}</div>
            <div class="datepicker__dropdown-times">
              <div
                v-for="(timeItem, index) in getAvailableTimes(date)"
                :key="`time-item-${index}`"
                :class="[
                  'datepicker__dropdown-time-item',
                  { 'datepicker__dropdown-time-item_disabled': timeItem.disabled },
                  { 'datepicker__dropdown-time-item_selected': isTimeSelected(timeItem) }
                ]"
                @click="selectTime(timeItem)"
              >
                {{ timeItem.value }}
              </div>
            </div>
            <div class="datepicker__dropdown-button-container">
              <div
                class="datepicker__dropdown-back-to-calendar-button"
                @click="isPickingTime = false"
              >
                <ChevronIcon class="datepicker__dropdown-back-to-calendar-button-arrow" />
                <div class="datepicker__dropdown-back-to-calendar-button-text">{{ buttonsText.backToCalendar }}</div>
              </div>
            </div>
            <div class="datepicker__dropdown-button-container">
              <BaseButton
                class="datepicker__dropdown-button"
                type="secondary"
                @click="hideDropdown"
              >
                {{ buttonsText.submit }}
              </BaseButton>
            </div>
          </div>
          <!-- TIME -->
        </transition>
      </div>
    </transition>
    <!-- DROPDOWN -->

    <transition name="slide-from-top">
      <span
        v-if="error && errorMsg"
        class="datepicker__error-msg"
      >
        {{ errorMsg }}
      </span>
    </transition>
  </div>
</template>

<script>
  import { format, getDay, differenceInMinutes } from 'date-fns'
  import { enUS, uk } from 'date-fns/locale'
  import { uk as datapickerUk, ro as datapickerRo, ru as datapickerRu, en as datapickerEn } from 'vuejs-datepicker/dist/locale'
  import Datepicker from 'vuejs-datepicker'
  import ChevronIcon from '@/assets/img/icons/chevron.svg?inline'
  import { mixin as clickaway } from 'vue-clickaway'
  import { cloneDeep, get } from 'lodash'
  import { mapState } from 'vuex'
  import * as ru from 'date-fns/locale/ru/index.js'
  import * as ro from 'date-fns/locale/ro/index.js'

  export default {
    name: 'BaseDatePicker',
    components: {
      Datepicker,
      ChevronIcon
    },
    mixins: [ clickaway ],
    props: {
      label: {
        type: String,
        default: ''
      },
      errorMsg: {
        type: String,
        default: ''
      },
      required: {
        type: Boolean,
        default: false
      },
      buttonsText: {
        type: Object,
        required: true,
        validator (buttonsText) {
          return 'pickTime' in buttonsText &&
            'submit' in buttonsText &&
            'backToCalendar' in buttonsText
        }
      },
      options: {
        type: Object,
        required: true,
        validator (options) {
          return 'schedule' in options &&
            'enabled' in options.schedule
        }
      }
    },

    data () {
      return {
        date: null,
        time: '',
        datesWithNoAvailableTime: [],
        isDropdownVisible: false,
        isPickingTime: false,
        datapickerLang: datapickerUk,
        error: null
      }
    },

    computed: {
      ...mapState(['currentLang']),
      classes () {
        return [
          'datepicker',
          {
            'datepicker_active': this.isDropdownVisible,
            'datepicker_error': this.error
          }
        ]
      },
      datepickerOptions () {
        return {
          'monday-first': true,
          'full-month-name': true,
          'maximum-view': 'day',
          'use-utc': true,
          'inline': true,
          'language': this.datapickerLang,
          'format': this.formatDate,
          'disabledDates': {
            to: new Date(new Date().setDate(new Date().getDate() + this.options.requestServiceIn)),
            days: this.disabledWeekDays,
            dates: [...this.datesWithNoAvailableTime, new Date()]
          }
        }
      },
      dropdownInsideTransitionName () {
        return this.isPickingTime
          ? 'slide-to-left'
          : 'slide-to-right'
      },
      dateTime () {
        // resulting timestamp (emiting value from component)
        const date = cloneDeep(this.date)
        const hours = Number(this.time.split(':')[0])
        const minutues = Number(this.time.split(':')[1])
        date.setHours(hours, minutues, 0, 0)
        return date.getTime()
      },
      disabledWeekDays () {
        // format to US day style
        // from 0 = Monday, 6 = Sunday
        // to 0 = Sunday, 6 = Saturday
        return this.options.schedule.disabled.map(dayIndex => {
          return dayIndex === 6
            ? 0
            : dayIndex + 1
        })
      }
    },

    watch: {
      errorMsg: {
        handler (msg) {
          this.error = !!msg
        },
        immediate: true
      },

      $route (to) {
        if (to.name === 'service-request') {
          this.initDatePicker()
        }
      }
    },

    created () {
      if (this.currentLang === 'ro') {
        this.datapickerLang = datapickerRo
        this.timepickerLang = ro
      } else if (this.currentLang === 'ru') {
        this.datapickerLang = datapickerRu
        this.timepickerLang = ru
      } else if (this.currentLang === 'uk') {
        this.datapickerLang = datapickerUk
        this.timepickerLang = uk
      } else if (this.currentLang === 'en') {
        this.datapickerLang = datapickerEn
        this.timepickerLang = enUS
      } else {
        if (this.currentCountry === 'md') {
          this.datapickerLang = datapickerRo
          this.timepickerLang = ro
        } else if (this.currentCountry === 'ru') {
          this.datapickerLang = datapickerRu
          this.timepickerLang = ru
        }
      }
      this.initDatePicker()
    },

    methods: {
      initDatePicker () {
        // select first available date and time
        this.onDateChange(this.getFirstAvailableDate())
      },
      getFirstAvailableDate () {
        const today = new Date()
        const tomorrow = new Date()
        tomorrow.setDate(today.getDate() + 1)

        let availableDate = tomorrow

        while (!this.getFirstAvailableTime(availableDate)) {
          const thisDay = cloneDeep(availableDate)
          const nextDay = cloneDeep(thisDay)
          nextDay.setDate(thisDay.getDate() + 1)

          // disable day on calendar if it has no available time
          this.datesWithNoAvailableTime = [...this.datesWithNoAvailableTime, cloneDeep(thisDay)]

          availableDate = cloneDeep(nextDay)
        }

        return availableDate
      },
      getFirstAvailableTime (date) {
        const availableTime = this.getAvailableTimes(date).find(time => {
          return !('disabled' in time) || !time.disabled
        })

        return get(availableTime, 'value')
      },
      getAvailableTimes (date) {
        const selectedWeekDayIndex = getDay(date) === 0
          ? 6
          : getDay(date) - 1

        const isToday = () => {
          return date.getFullYear() === new Date().getFullYear() &&
            date.getMonth() === new Date().getMonth() &&
            date.getDate() === new Date().getDate()
        }

        if (isToday()) {
          // disable already passed times
          return this.options.schedule.enabled[selectedWeekDayIndex]
            ? this.options.schedule.enabled[selectedWeekDayIndex]
              .map(time => {
                const timeToCompare = new Date()
                const hours = Number(time.value.split(':')[0])
                const minutues = Number(time.value.split(':')[1])
                timeToCompare.setHours(hours, minutues, 0, 0)

                const isTimePass = differenceInMinutes(timeToCompare, new Date()) < 0

                return isTimePass
                  ? { ...time, disabled: true }
                  : time
              })
            : []
        }

        return this.options.schedule.enabled[selectedWeekDayIndex] || []
      },

      formatTime (date) {
        return format(date, 'HH:mm', { locale: this.timepickerLang })
      },
      formatDate (date) {
        return format(date, 'd MMMM yyyy', { locale: this.timepickerLang })
      },

      onDateChange (date) {
        this.date = date
        this.$emit('input', this.dateTime)
        this.onTimeChange(this.getFirstAvailableTime(date))
      },
      onTimeChange (time) {
        this.time = time
        this.$emit('input', this.dateTime)
      },

      onPickTime () {
        this.isPickingTime = true
      },
      isTimeSelected (time) {
        return this.time === time.value
      },
      selectTime (time) {
        if (time.disabled) return
        this.onTimeChange(time.value)
      },

      toggleDropdownVisibility () {
        this.error = false
        this.isDropdownVisible
          ? this.hideDropdown()
          : this.showDropdown()
      },
      showDropdown () {
        this.isPickingTime = false
        this.isDropdownVisible = true
      },
      hideDropdown () {
        this.isDropdownVisible = false
        this.isPickingTime = false
      }
    }
  }
</script>

<style lang="scss">
$padding-for-error: 25px;

.datepicker {
  position: relative;
  display: block;
  min-width: 290px;
  max-width: 335px;
  padding-bottom: $padding-for-error;
  cursor: pointer;

  @include mobile {
    max-width: 100%;
  }

  &__label {
    display: inline-block;
    padding-bottom: 1em;
    font-family: "FavoritC", Arial, Helvetica, sans-serif;
    font-size: 16px;
    color: $c-input-grey;

    &-required {
      color: $c-active-light;
    }
  }

  &__field {
    position: relative;
    display: block;
    width: 100%;
    padding: 14px 18px;
    font-family: "FavoritC", Arial, Helvetica, sans-serif;
    font-size: 16px;
    line-height: 24px;
    background-color: $c-white;
    border: 1px solid $c-gray-medium;
    outline: none;
    transition: 0.3s;
    caret-color: $c-black;
  }

  &__arrow {
    position: absolute;
    right: 22px;
    bottom: 42%;
    width: 14px;
    height: 7px;
    overflow: visible;
    transition: 0.3s;
  }

  &__date {
    margin-right: 5px;
  }

  &__dropdown {
    position: absolute;
    top: calc(100% - 20px);
    left: 50%;
    z-index: 9999;
    width: 100%;
    max-width: 335px;
    padding: 15px 10px 0;
    cursor: default;
    user-select: none;
    background-color: $c-gray-light;
    transform: translateX(-50%);

    @include mobile {
      max-width: 338px;
      padding: 5px 0;
    }

    .vdp-datepicker__calendar {
      width: 100%;
      padding-left: 4px;
      margin: 0 auto;
      font-family: "FavoritC", Arial, Helvetica, sans-serif;
      background: transparent;
      border: none;

      @include mobile {
        padding-left: 27px;
        transform: translateX(-13px);
      }

      @include mobileXs {
        padding-left: 0;
        transform: translateX(1px);
      }

      header {
        display: flex;
        justify-content: space-between;
        padding: 0 15%;

        .day__month_btn.up {
          transition: color 0.3s;

          &:hover {
            color: $c-active;
            background: transparent;
          }
        }
      }

      .prev,
      .next {
        overflow: hidden;
        background: url('../../assets/img/icons/chevron.svg') no-repeat center;
        background-size: 15px;
        opacity: 0.7;
        transition: background 0.3s, opacity 0.3s;
        transform: rotate(-90deg);

        &::after {
          display: none;
        }

        &:hover:not(.disabled) {
          background: url('../../assets/img/icons/chevron.svg') no-repeat center;
          background-size: 15px;
          opacity: 1;
        }

        &.disabled {
          opacity: 0.2;
        }
      }

      .prev {
        transform: rotate(90deg);
      }

      .cell {
        border: none !important;
        transition: all 0.3s;

        &.month {
          &:hover {
            color: $c-active-light;
            background: transparent;
          }
        }

        &.selected {
          color: $c-white !important;
          background: $c-black;
        }

        &.weekend {
          &:hover {
            color: $c-white;
          }
        }

        &.day {
          width: 32px;
          height: 32px;
          padding: 0;
          margin: 1px 6.2px 0;
          line-height: 32px;

          &:hover {
            color: $c-white;
            background: $c-black !important;
          }

          @include mobileXs {
            margin: 1px 4.6px 0;
          }
        }

        &.day-header,
        &.blank {
          color: $c-gray-dark;

          &:hover {
            color: $c-gray-dark;
            cursor: default;
            background: transparent;
          }
        }

        &.disabled {
          color: $c-gray-medium;
          pointer-events: none;
        }
      }
    }

    &-button {
      &-container {
        display: flex;
        justify-content: center;
        margin: 20px 0 30px;
      }

      background-color: $c-gray-light;
      transition: all 0.4s;
    }

    &-time {
      padding-right: 10px;
      padding-left: 10px;
      font-family: "FavoritC", Arial, Helvetica, sans-serif;

      &-title {
        margin: 10px 0 25px;
        text-align: center;
      }
    }

    &-times {
      display: flex;
      flex-wrap: wrap;
      margin-top: 10px;

      .datepicker__dropdown-time-item {
        flex-basis: calc(33% - 8px);
      }
    }

    &-time-item {
      flex: 0 1 20%;
      padding: 4px;
      margin-bottom: 25px;
      color: rgba($c-black, 0.3);
      text-align: center;
      cursor: pointer;
      transition: all 0.3s;

      &:hover {
        color: $c-black;
      }

      &_disabled {
        color: $c-gray-medium;
        pointer-events: none;
      }

      &_selected {
        color: $c-white;
        background-color: $c-black;

        &:hover {
          color: $c-white;
        }
      }
    }

    &-back-to-calendar-button {
      display: flex;
      align-items: center;
      margin-right: 5px;
      cursor: pointer;

      &-arrow {
        width: 16px;
        height: 16px;
        margin-right: 10px;
        transition: 0.3s;
      }

      &-text {
        font-size: 12px;
        line-height: 16px;
        transition: color 0.3s;
      }

      &:hover {
        .datepicker__dropdown-back-to-calendar-button {
          &-text {
            color: $c-active;
          }

          &-arrow {
            stroke: $c-active;
          }
        }
      }
    }
  }

  &__error-msg {
    position: absolute;
    top: 77%;
    left: 0;
    z-index: 1;
    font-size: 12px;
    color: $c-active-light;
  }

  &_error {
    .datepicker {
      &__field {
        background-color: $c-error-light;
      }
    }
  }

  &_active {
    .datepicker {
      &__arrow {
        transform: rotate(-180deg);
      }
    }
  }
}

.calendar-transition {
  &-enter-active,
  &-leave-active {
    transition: 0.5s;
  }

  &-enter,
  &-leave-to {
    opacity: 0;
    transform: translate(-50%, -15px);
  }
}
</style>
